// src/components/dashboard/RecentLeads.js
import React from 'react';
import styled from 'styled-components';
import { 
  FaUserPlus, 
  FaPhoneAlt, 
  FaEnvelope, 
  FaMapMarkerAlt,
  FaBriefcase,
  FaCalendarAlt,
  FaExternalLinkAlt
} from 'react-icons/fa';
import DashboardCard from './DashboardCard';
import { useNavigate } from 'react-router-dom';

const LeadWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(2)};
`;

const LeadItem = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const LeadHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(2)};
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${props => props.theme.spacing(1)};
  }
`;

const LeadName = styled.div`
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.medium};
`;

const LeadDate = styled.div`
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(0.5)};
`;

const LeadDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(2)};
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const LeadDetail = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
  
  svg {
    color: ${props => props.theme.colors.primary};
    flex-shrink: 0;
  }
`;

const LeadServices = styled.div`
  margin-top: ${props => props.theme.spacing(1)};
  padding-top: ${props => props.theme.spacing(1)};
  border-top: 1px solid ${props => props.theme.colors.inputGray}33;
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
  
  .service-title {
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    margin-bottom: ${props => props.theme.spacing(0.5)};
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing(0.5)};
  }
  
  .service-item {
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing(1)};
    margin-top: ${props => props.theme.spacing(0.5)};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing(4)};
  color: ${props => props.theme.colors.textSecondary};
`;

const ViewButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(0.5)};
  background-color: ${props => props.theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(1)} ${props => props.theme.spacing(2)};
  cursor: pointer;
  font-size: ${props => props.theme.typography.fontSize.small};
  margin-left: auto;
  
  &:hover {
    opacity: 0.9;
  }
`;

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

// Helper to format phone numbers
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phoneNumber;
};

const RecentLeads = ({ data, loading, error }) => {
  const navigate = useNavigate();

  const handleLeadClick = (leadId) => {
    navigate(`/leads/view/${leadId}`);
  };

  return (
    <DashboardCard
      title="Recent Leads"
      icon={<FaUserPlus />}
      viewAllLink="/leads"
      loading={loading}
      error={error}
    >
      {data && data.length > 0 ? (
        <LeadWrapper>
          {data.map((lead) => (
            <LeadItem 
              key={lead.id} 
              onClick={() => handleLeadClick(lead.id)}
            >
              <LeadHeader>
                <LeadName>{lead.name}</LeadName>
                <LeadDate>
                  <FaCalendarAlt />
                  {formatDate(lead.createdAt)}
                </LeadDate>
              </LeadHeader>
              
              <LeadDetails>
                <LeadDetail>
                  <FaPhoneAlt />
                  {formatPhoneNumber(lead.phone)}
                </LeadDetail>
                
                {lead.email && (
                  <LeadDetail>
                    <FaEnvelope />
                    {lead.email}
                  </LeadDetail>
                )}
                
                <LeadDetail>
                  <FaMapMarkerAlt />
                  {lead.city}, {lead.state}
                </LeadDetail>
                
                {lead.origin && (
                  <LeadDetail>
                    <FaBriefcase />
                    {lead.origin.origin_name}
                  </LeadDetail>
                )}
              </LeadDetails>
              
              {lead.serviceRequests && lead.serviceRequests.length > 0 && (
                <LeadServices>
                  <div className="service-title">
                    <FaBriefcase />
                    Services Requested:
                  </div>
                  
                  {lead.serviceRequests.map((request, index) => (
                    <div key={index} className="service-item">
                      - {request.Service?.service_name || 'Unknown Service'}
                    </div>
                  ))}
                </LeadServices>
              )}
              
              <ViewButton onClick={(e) => {
                e.stopPropagation();
                handleLeadClick(lead.id);
              }}>
                View Details
                <FaExternalLinkAlt />
              </ViewButton>
            </LeadItem>
          ))}
        </LeadWrapper>
      ) : (
        <EmptyState>No recent leads found</EmptyState>
      )}
    </DashboardCard>
  );
};

export default RecentLeads;